import { useState } from "react";

import Sentry from "@similarweb/sentry-logger";
import { Button, PlainTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { buildKeywordGroupKeywordListId } from "app/modules/demand-analysis/keyword-lists";
import { BasicDurations } from "app/services/DurationService";
import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useKeywordGroupNameContext } from "./KeywordGroupNameContext";
import { useKeywordListComposerContext } from "./KeywordListComposerContext";
import { trackPopupClick } from "./tracking";

export const KeywordListComposerCreatorFooter = ({ onClose }: { onClose: VoidFunction }) => {
    const t = useTranslation();
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const { suggestionsStatus, keywords, country } = useKeywordListComposerContext();
    const { keywordGroupName, keywordGroupNameValidationStatus } = useKeywordGroupNameContext();
    const defaultErrorMessage = t("keywordanalysis.keywordgroup.wizard.save.button.tooltip.missing.name");
    const { isValid, errorMessage } = keywordGroupNameValidationStatus;
    const isDisabled =
        suggestionsStatus === "pending" || !isValid || keywords.length === 0 || keywordGroupName.length === 0;

    const handleClick = async () => {
        setIsCreatingGroup(true);

        try {
            const newGroup = await keywordGroupsQueryClient.createGroup({
                ["Name"]: keywordGroupName,
                ["Keywords"]: keywords.map(({ term }) => term),
            });

            if (newGroup) {
                await proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
                    id: [buildKeywordGroupKeywordListId(newGroup.Id)],
                    country: country.id,
                    duration: BasicDurations.LAST_TWELVE_MONTHS,
                    ignoreFilterConsistency: true,
                });
            } else {
                // KeywordGroupsService.createKeywordGroup() swallows errors if a keyword group already exists,
                // so we need to throw an error here to correctly handle the exception
                throw new Error("[Keyword List Composer] newGroup is undefined");
            }

            trackPopupClick({ event: "Save", keywordListName: keywordGroupName });
        } catch (e: unknown) {
            // Refetch keyword groups to ensure we validate the keyword group name against the latest data
            void keywordGroupsQueryClient.invalidateAll();

            if (e instanceof Error) {
                Sentry.error("[Keyword List Composer] Error trying to create keyword group: ", e, {
                    tags: { team: "ants" },
                });
            } else {
                Sentry.error(
                    "[Keyword List Composer] Unknown error trying to create keyword group: ",
                    new Error("Unknown error"),
                    { tags: { team: "ants" } },
                );
            }
        } finally {
            setIsCreatingGroup(false);
        }
    };

    return (
        <Flex
            justifyContent="flex-end"
            gap="8px"
            padding="16px 0 0"
        >
            <Button
                type="flatDark"
                onClick={onClose}
            >
                {t("common.close")}
            </Button>
            <PlainTooltip
                tooltipContent={errorMessage ?? defaultErrorMessage}
                enabled={isDisabled}
            >
                <div>
                    <Button
                        type="primary"
                        onClick={handleClick}
                        isDisabled={isDisabled}
                        isLoading={isCreatingGroup}
                    >
                        {t("auto.keyword.list.generator.footer.submit")}
                    </Button>
                </div>
            </PlainTooltip>
        </Flex>
    );
};
